/**
 * Regras do painel de declarações de EPI do LTCAT (#468), sem tela: o que é uma célula, o que
 * mudou em relação ao que o painel viu, e o que fazer com o rascunho quando a resposta do
 * servidor diverge dele.
 *
 * Os ids chegam como string do servidor mesmo com o tipo dizendo number: toda chave de célula
 * passa por `String(...)` e o corpo do PUT volta a `Number(...)`.
 */
import type {
  LtcatEpiDeclaracaoAlteracao,
  LtcatEpiDeclaracaoChave,
  LtcatEpiDeclaracoesConflito,
  LtcatEpiDeclaracoesPainel,
  LtcatEpiResposta,
} from '../../../types/document';

/** Âncora do painel: a pendência de respostas faltando rola até ela. */
export const ID_DO_PAINEL_DE_DECLARACOES_EPI = 'painel-declaracoes-epi';
export const CODIGO_DA_PENDENCIA_DE_DECLARACOES_EPI = 'ltcat-epi-declaracoes-pendentes';

export type RespostaDaCelula = LtcatEpiResposta | null;

/** Respostas por célula, indexadas por `chaveDaCelula`. */
export type MapaDeRespostas = Record<string, RespostaDaCelula>;

export type ConflitoDeCelula = LtcatEpiDeclaracoesConflito['conflitos'][number];

export const CHAVES_DAS_DECLARACOES: readonly LtcatEpiDeclaracaoChave[] = ['D1', 'D2', 'D3', 'D4', 'D5'];

export const ROTULO_DA_RESPOSTA: Record<LtcatEpiResposta, string> = {
  S: 'Sim',
  N: 'Não',
  NA: 'Não aplicável',
};

export const ROTULO_DE_PENDENTE = 'Pendente';

export const rotuloDaResposta = (resposta: RespostaDaCelula): string =>
  resposta === null ? ROTULO_DE_PENDENTE : ROTULO_DA_RESPOSTA[resposta];

export const chaveDaCelula = (occupationId: number | string, chave: LtcatEpiDeclaracaoChave): string =>
  `${String(occupationId)}:${chave}`;

/** As respostas que o servidor devolveu, uma por célula. */
export const respostasDoPainel = (painel: LtcatEpiDeclaracoesPainel): MapaDeRespostas => {
  const mapa: MapaDeRespostas = {};
  painel.setores.forEach((setor) =>
    setor.cargos.forEach((cargo) =>
      CHAVES_DAS_DECLARACOES.forEach((chave) => {
        mapa[chaveDaCelula(cargo.occupationId, chave)] = cargo.respostas[chave] ?? null;
      })
    )
  );
  return mapa;
};

export const respostaVigente = (
  carregadas: MapaDeRespostas,
  rascunho: MapaDeRespostas,
  celula: string
): RespostaDaCelula => (celula in rascunho ? rascunho[celula] : (carregadas[celula] ?? null));

/** Guarda a escolha no rascunho; escolher de volta o que o painel viu desfaz a alteração. */
export const escolher = (
  rascunho: MapaDeRespostas,
  carregadas: MapaDeRespostas,
  celula: string,
  resposta: RespostaDaCelula
): MapaDeRespostas => {
  const proximo = { ...rascunho };
  if (resposta === (carregadas[celula] ?? null)) delete proximo[celula];
  else proximo[celula] = resposta;
  return proximo;
};

/** As células alteradas, cada uma com o valor que o painel viu (`previous`). */
export const alteracoesDoRascunho = (
  painel: LtcatEpiDeclaracoesPainel,
  rascunho: MapaDeRespostas
): LtcatEpiDeclaracaoAlteracao[] => {
  const carregadas = respostasDoPainel(painel);
  const alteracoes: LtcatEpiDeclaracaoAlteracao[] = [];
  painel.setores.forEach((setor) =>
    setor.cargos.forEach((cargo) =>
      CHAVES_DAS_DECLARACOES.forEach((chave) => {
        const celula = chaveDaCelula(cargo.occupationId, chave);
        if (!(celula in rascunho)) return;
        alteracoes.push({
          occupationId: Number(cargo.occupationId),
          declarationKey: chave,
          previous: carregadas[celula] ?? null,
          answer: rascunho[celula],
        });
      })
    )
  );
  return alteracoes;
};

/**
 * Depois de recarregar com rascunho em mãos (alcance ou período mudou): só sobrevive a escolha
 * cuja célula ainda existe e cujo valor no servidor é o mesmo que ela viu. Qualquer outra teria
 * um `previous` que a pessoa nunca viu.
 */
export const reconciliarRascunho = (
  antigo: LtcatEpiDeclaracoesPainel,
  novo: LtcatEpiDeclaracoesPainel,
  rascunho: MapaDeRespostas
): MapaDeRespostas => {
  const antes = respostasDoPainel(antigo);
  const agora = respostasDoPainel(novo);
  const mantido: MapaDeRespostas = {};
  Object.entries(rascunho).forEach(([celula, resposta]) => {
    if (celula in agora && agora[celula] === antes[celula] && resposta !== agora[celula]) {
      mantido[celula] = resposta;
    }
  });
  return mantido;
};

/** O painel com os valores atuais do servidor nas células que divergiram. */
export const aplicarConflitos = (
  painel: LtcatEpiDeclaracoesPainel,
  conflitos: ConflitoDeCelula[]
): LtcatEpiDeclaracoesPainel => {
  const atuais: MapaDeRespostas = {};
  conflitos.forEach((conflito) => {
    atuais[chaveDaCelula(conflito.occupationId, conflito.declarationKey)] = conflito.atual;
  });
  return {
    ...painel,
    setores: painel.setores.map((setor) => ({
      ...setor,
      cargos: setor.cargos.map((cargo) => {
        const respostas = { ...cargo.respostas };
        CHAVES_DAS_DECLARACOES.forEach((chave) => {
          const celula = chaveDaCelula(cargo.occupationId, chave);
          if (celula in atuais) respostas[chave] = atuais[celula];
        });
        return { ...cargo, respostas };
      }),
    })),
  };
};

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null;

/**
 * Os conflitos do 409. O corpo do erro chega em `details`, e o servidor repete `details` dentro
 * dele; aceita os dois níveis.
 */
export const conflitosDoErro = (erro: unknown): ConflitoDeCelula[] | null => {
  if (!ehRegistro(erro) || erro.status !== 409 || !ehRegistro(erro.details)) return null;
  const aninhado = erro.details.details;
  const lista = (ehRegistro(aninhado) ? aninhado.conflitos : undefined) ?? erro.details.conflitos;
  return Array.isArray(lista) ? (lista as ConflitoDeCelula[]) : null;
};

/** Quantas células o servidor ainda tem como Pendente. */
export const totalSemResposta = (painel: LtcatEpiDeclaracoesPainel): number =>
  Object.values(respostasDoPainel(painel)).filter((resposta) => resposta === null).length;
